<script setup>
import { computed, ref, watch } from "vue";
import { useVuelidate } from "@vuelidate/core";
import { required, helpers, sameAs, not } from "@vuelidate/validators";
import { ArgonLoadingButton } from "@/components";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonSnackbar from "@/components/ArgonSnackbar.vue";
import { useUserStore } from "@/_store/userStore.js";
import { errorUtils } from "@/utils";

const userStore = useUserStore();
const credentials = ref({
  password: "",
  newPassword: "",
  confirmPassword: "",
});
const loading = ref(false);
const snackbar = ref(null);

const newPass = computed(() => credentials.value.newPassword);
const currentPass = computed(() => credentials.value.password);

const rules = {
  password: {
    required: helpers.withMessage("Contraseña  es requerido", required),
  },
  newPassword: {
    required: helpers.withMessage("Nueva contraseña es requerido", required),
    different: helpers.withMessage(
      "La Nueva contraseña tiene que ser diferente a la actual",
      not(sameAs(currentPass))
    ),
  },
  confirmPassword: {
    required: helpers.withMessage(
      "Confirmar Contraseña es requerido",
      required
    ),
    sameAsNewPassword: helpers.withMessage(
      "Nueva contraseña y Confirmar contraseña deben coincidir",
      sameAs(newPass)
    ),
  },
};

const v$ = ref(useVuelidate(rules, credentials));

async function validate() {
  await v$.value.$validate();
  return v$.value.$errors;
}

function closeSnackbar() {
  snackbar.value = null;
}

function resetForm() {
  credentials.value.password = "";
  credentials.value.newPassword = "";
  credentials.value.confirmPassword = "";
  v$.value.$reset();
}

function change() {
  validate().then((values) => {
    let message = "";

    for (const value of values) {
      message = `${message}<li>${value.$message}</li>`;
    }

    if (message !== "") {
      snackbar.value = {
        title: "Error",
        message: `<ul>${message}</ul>`,
        type: "danger",
        color: "white",
      };
      return;
    }

    loading.value = true;
    userStore.error = null;
    userStore.changedPassword = null;

    const contract = {
      ...credentials.value,
      user: userStore.userSigned.email,
    };

    userStore.changePassword(contract);
  });
}

watch(
  () => userStore.error,
  (value) => {
    if (value) {
      snackbar.value = {
        title: "Error",
        message: errorUtils.getErrorMessageHtml(value),
        type: "danger",
        color: "white",
      };
      loading.value = false;
    }
  }
);

watch(
  () => userStore.changedPassword,
  (value) => {
    if (value) {
      snackbar.value = {
        title: "Guardado",
        message: "Su contraseña ha sido actualizada.",
        type: "info",
        color: "white",
      };
      loading.value = false;
      resetForm();
    }
  }
);
</script>

<template>
  <div class="card h-100">
    <div class="card-header pb-0"><p class="mb-0">Cambiar contraseña</p></div>
    <div class="card-body">
      <form role="form">
        <div class="row">
          <div class="col-details">
            <label for="password" class="col-form-label">Actual</label>
          </div>
          <div class="col">
            <argon-input
              id="password"
              v-model="credentials.password"
              type="password"
              :error="v$.password.$error"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-details">
            <label for="new-password" class="col-form-label">Nueva</label>
          </div>
          <div class="col">
            <argon-input
              id="new-password"
              v-model="credentials.newPassword"
              type="password"
              :error="v$.newPassword.$error"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-details">
            <label for="confirm-password" class="col-form-label">Confirmar</label>
          </div>
          <div class="col">
            <argon-input
              id="confirm-password"
              v-model="credentials.confirmPassword"
              type="password"
              :error="v$.confirmPassword.$error"
            />
          </div>
        </div>
      </form>
    </div>
    <div class="card-footer">
      <div class="float-end">
        <argon-loading-button
          class="btn btn-xs bg-gradient-primary m-0"
          color="primary"
          variant="gradient"
          :label="loading ? 'Guardando...' : 'Guardar'"
          :loading="loading"
          @click.prevent="change"
        />
      </div>
    </div>
  </div>
  <div class="position-fixed top-1 end-1 z-index-2">
    <argon-snackbar
      v-if="snackbar"
      :title="snackbar.title"
      :description="snackbar.message"
      :icon="{ component: 'ni ni-notification-70', color: snackbar.type }"
      :color="snackbar.color"
      :close-handler="closeSnackbar"
    />
  </div>
</template>

<style scoped>
.col-details {
  flex: 0 0 auto;
  width: 90px !important;
}
</style>
